import type {
  EntryView,
  EventCocodePitInput,
  EventInput,
  EventStatus,
  PrizeInput,
  StatsBreakdownRow,
  StatsSummary,
} from '@koto-event/shared';

/**
 * 管理画面 API クライアント。
 * 認証は HttpOnly Cookie のため credentials: 'include' を必ず付ける。
 */

const BASE = import.meta.env.VITE_API_BASE ?? '';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      // 本文のない POST（公開・一時停止など）に Content-Type を付けると、空の本文として拒否されるため付けない
      ...(init?.body && !(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (!res.ok) {
    let code = 'UNKNOWN';
    let message = '通信に失敗しました';
    try {
      const body = (await res.json()) as { error?: { code?: string; message?: string } };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      /* JSON でない場合は既定のメッセージを使う */
    }
    throw new ApiError(res.status, code, message);
  }

  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return (await res.text()) as T;
  return (await res.json()) as T;
}

function query(params: Record<string, string | undefined>): string {
  const q = new URLSearchParams(Object.entries(params).filter((e): e is [string, string] => Boolean(e[1])));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export interface AdminUserView {
  id: string;
  email: string;
  name: string;
}

export interface EventRow {
  id: string;
  name: string;
  status: EventStatus;
  lotteryType: string;
  entryRule: string;
  startAt: string;
  endAt: string;
  cocodePitCount: number;
  entryCount: number;
}

export interface PrizeSetting extends PrizeInput {
  id: string;
  remaining: number | null;
}

export interface EventCocodePitRow extends EventCocodePitInput {
  /** タッチ時に CocodePit の location から記録した店舗名 */
  locationName: string;
  touchCount: number;
  entryCount: number;
}

export interface EventDetail extends Omit<EventInput, 'startAt' | 'endAt'> {
  id: string;
  status: EventStatus;
  startAt: string;
  endAt: string;
  prizes: PrizeSetting[];
  cocodePits: EventCocodePitRow[];
  cocodePitSettings: { okUrl: string; ngUrl: string; validPeriodMinutes: number };
  /** テスト用の入口を発行できる環境か（local / development のみ） */
  testTouchEnabled: boolean;
}

export interface AdminEntryRow extends EntryView {
  eventName: string;
  pcode: string;
  stampCode: string | null;
  rewardGrantRef: string | null;
}

type StatsParams = { from?: string; to?: string; eventId?: string };

export const api = {
  // --- 認証 ---------------------------------------------------------------
  login: (email: string, password: string) =>
    request<{ user: AdminUserView }>('/api/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: true }>('/api/admin/auth/logout', { method: 'POST' }),
  me: () => request<{ user: AdminUserView }>('/api/admin/auth/me'),

  // --- イベント -----------------------------------------------------------
  listEvents: () => request<{ items: EventRow[] }>('/api/admin/events'),
  getEvent: (id: string) => request<EventDetail>(`/api/admin/events/${id}`),
  createEvent: (event: EventInput, prizes: PrizeInput[], cocodePits: EventCocodePitInput[]) =>
    request<{ id: string }>('/api/admin/events', {
      method: 'POST',
      body: JSON.stringify({ event, prizes, cocodePits }),
    }),
  updateEvent: (
    id: string,
    event: Partial<EventInput>,
    prizes?: PrizeInput[],
    cocodePits?: EventCocodePitInput[],
  ) =>
    request<{ id: string }>(`/api/admin/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ event, ...(prizes ? { prizes } : {}), ...(cocodePits ? { cocodePits } : {}) }),
    }),
  setEventStatus: (id: string, action: 'publish' | 'pause' | 'end') =>
    request<{ id: string; status: EventStatus }>(`/api/admin/events/${id}/${action}`, { method: 'POST' }),
  /** テスト用の入口（実物の CocodePit なしで LIFF を開くための URL）を発行する。開発環境のみ */
  issueTestTouch: (id: string, pcode: string) =>
    request<{ liffUrl: string; expiresAt: string }>(`/api/admin/events/${id}/test-touch`, {
      method: 'POST',
      body: JSON.stringify({ pcode }),
    }),

  // --- 抽選結果 -----------------------------------------------------------
  listEntries: (params: { q?: string; eventId?: string; status?: string; rewardGrantStatus?: string }) =>
    request<{ items: AdminEntryRow[]; manualConfirmEnabled: boolean }>(`/api/admin/entries${query(params)}`),
  confirmEntry: (id: string, stampCode: string | null) =>
    request<EntryView>(`/api/admin/entries/${id}/confirm`, {
      method: 'POST',
      body: JSON.stringify({ stampCode }),
    }),
  retryGrant: (id: string) => request<EntryView>(`/api/admin/entries/${id}/grant-retry`, { method: 'POST' }),

  // --- 集計 ---------------------------------------------------------------
  statsSummary: (params: StatsParams) => request<StatsSummary>(`/api/admin/stats/summary${query(params)}`),
  statsByLocation: (params: StatsParams) =>
    request<{ items: StatsBreakdownRow[] }>(`/api/admin/stats/by-location${query(params)}`),
  statsByCocodePit: (params: StatsParams) =>
    request<{ items: StatsBreakdownRow[] }>(`/api/admin/stats/by-cocodepit${query(params)}`),
  entriesCsvUrl: (params: StatsParams) => `${BASE}/api/admin/stats/entries.csv${query(params)}`,

  // --- 画像アップロード ----------------------------------------------------
  uploadMedia: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return request<{ url: string }>('/api/admin/media', { method: 'POST', body: form });
  },
};
